/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Displays a comprehensive list of all bikes in the fleet. Includes 
filtering, sorting, and pagination capabilities for efficient fleet management.
*/

import React, { useState, ChangeEvent } from "react";
import BikesHeader from "./components/BikesHeader";
import BikesControls from "./components/BikesControls";
import BikesTable from "./components/BikesTable";
import BikesList from "./components/BikesList";
import BikeModelsTable from "./components/BikeModelsTable";
import DeleteBikeDialog from "./components/DeleteBikeDialog";
import NoFilteredBikesAlert from "./components/NoFilteredBikesAlert";
import { useBikesData } from "./hooks/useBikesData";
import type { BikeFilter } from "./components/BikesControls";
import DashboardFiltersComponent, { DashboardFilters } from "../dashboard/components/DashboardFilters";

const BikesListPage: React.FC = () => {
  const [viewType, setViewType] = useState<"table" | "list">("table");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bikeToDelete, setBikeToDelete] = useState<{ id: string; name: string } | null>(null);
  const [locationFilters, setLocationFilters] = useState<DashboardFilters>({
    regionId: "all",
    organizationId: "all",
    siteId: "all"
  });
  
  const {
    bikes,
    isLoading,
    refetch,
    searchTerm,
    handleSearch,
    currentPage,
    pageSize,
    handlePageChange,
    activeFilter,
    applyFilter,
    clearFilter,
    handleExport,
    totalItems
  } = useBikesData(locationFilters);
  
  const handleDeleteClick = (bikeId: string, bikeName: string) => {
    setBikeToDelete({ id: bikeId, name: bikeName });
    setDeleteDialogOpen(true);
  };
  
  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    refetch();
    setBikeToDelete(null);
  };

  // Create wrapper functions to match the expected types
  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleSearch(e.target.value);
  };

  // Create a wrapper for applyFilter to ensure it handles BikeFilter
  const handleFilterApply = (filter: BikeFilter) => {
    applyFilter(filter);
  };

  return (
    <div className="space-y-6">
      <BikesHeader onRefresh={refetch} />
      
      <div className="rounded-lg border p-6 shadow-sm">
        <BikeModelsTable />
      </div>
      
      <DashboardFiltersComponent 
        filters={locationFilters}
        onFiltersChange={setLocationFilters}
      />
      
      <BikesControls
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        viewType={viewType}
        onViewTypeChange={(val) => setViewType(val)}
        activeFilter={activeFilter as BikeFilter}
        onFilterApply={handleFilterApply}
        onFilterClear={clearFilter}
        onExport={handleExport}
      />
      
      {viewType === "table" ? (
        <BikesTable
          bikes={bikes}
          isLoading={isLoading}
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onDeleteClick={handleDeleteClick}
        />
      ) : (
        <BikesList
          bikes={bikes}
          isLoading={isLoading}
          currentPage={currentPage}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onDeleteClick={handleDeleteClick}
        />
      )}

      {activeFilter && bikes.length === 0 && !isLoading && (
        <NoFilteredBikesAlert onClearFilter={clearFilter} />
      )}

      {bikeToDelete && (
        <DeleteBikeDialog
          open={deleteDialogOpen}
          bikeId={bikeToDelete.id}
          bikeName={bikeToDelete.name}
          onOpenChange={setDeleteDialogOpen}
          onDelete={handleDeleteConfirm}
          showDeleteToast={true}
        />
      )}
    </div>
  );
};

export default BikesListPage;
